/**
 * UFT GA4 DataLayer Analytics
 *
 * Self-contained module that listens to UFT EventManager events
 * and pushes corresponding GA4 events to window.dataLayer.
 *
 * Supported Events:
 * - product:viewed → GA4 view_item
 * - cart:item-added → GA4 add_to_cart
 */

window.UFT = window.UFT || {};
window.UFT.Analytics = window.UFT.Analytics || {};
window.UFT.Analytics.GA4DataLayer = (function () {
	class GA4DataLayer {
		constructor() {
			this.debug = false;
			this.currency = 'USD';
			this.eventManager = null;
		}

		init() {
			if (!this.validateEventManager()) {
				console.error('[UFT GA4 Analytics] EventManager not available. Analytics will not initialize.');
				return;
			}

			this.eventManager = window.UFT.EventManager;
			this.currency = this.getCurrency();

			this.subscribeToEvents();
			this.log('GA4 DataLayer Analytics initialized', null);
		}

		subscribeToEvents() {
			const events = this.eventManager.eventStrings;

			this.eventManager.on(events.PRODUCT_VIEWED, this.handleProductViewed.bind(this));
			this.eventManager.on(events.CART_ITEM_ADDED, this.handleCartItemAdded.bind(this));
			this.eventManager.on(events.NAV_CLICK, this.handleNavClickEvent.bind(this));

			this.log('Subscribed to product, cart, and navigation events', null);
		}

		handleProductViewed(event) {
			if (!this.validateEventData(event, ['productId', 'productName'])) {
				console.warn('[UFT GA4 Analytics] Invalid event data for view_item:', event);
				return;
			}

			const item = this.transformEventItemToGA4Item(event, 1);

			if (!item) {
				console.warn('[UFT GA4 Analytics] Failed to transform data to GA4 item for view_item:', event);
				return;
			}

			item.index = 0; //Index is 0 for view item events that happen in isolation
			this.log('Product Viewed - Mapped to GA4 Item', item);

			const totalPrice = this.centsToPrice(event.price);

			const eventObject = {
				event: 'view_item',
				ecommerce: {
					currency: this.currency,
					value: totalPrice,
					items: [item],
				},
			};

			this.pushToDataLayer(eventObject, true);
			this.log('view_item event fired', eventObject);
		}

		handleCartItemAdded(event) {
			if (!event || !Array.isArray(event.items) || event.items.length === 0) {
				console.warn('[UFT GA4 Analytics] Invalid event data for add_to_cart: items must be a non-empty array', event);
				return;
			}

			const { ga4Items, totalValueInCents } = this.mapEventItemsToGA4Items(event.items);
			if (ga4Items.length === 0) {
				console.warn('[UFT GA4 Analytics] No valid items to add to cart');
				return;
			}

			const totalPrice = this.centsToPrice(totalValueInCents);
			const eventObject = {
				event: 'add_to_cart',
				ecommerce: {
					currency: this.currency,
					value: totalPrice,
					items: ga4Items,
				},
			};

			this.pushToDataLayer(eventObject, true);
			this.log('add_to_cart event fired', eventObject);
		}

		mapEventItemsToGA4Items(eventItems) {
			const ga4Items = [];
			let totalValueInCents = 0;

			for (const item of eventItems) {
				if (!item.productId || !item.productName || !item.quantity || item.price == null) {
					console.warn('[UFT GA4 Analytics] Skipping invalid item in add_to_cart:', item);
					continue;
				}

				const ga4Item = this.transformEventItemToGA4Item(item, item.quantity);
				if (!ga4Item) {
					console.warn('[UFT GA4 Analytics] Failed to transform item to GA4 format:', item);
					continue;
				}

				ga4Items.push(ga4Item);
				totalValueInCents += item.price * item.quantity;
			}

			return {
				ga4Items,
				totalValueInCents,
			};
		}

		/**
		 * Transform event data to GA4 item format
		 *
		 * @param {Object} eventItem - Item from EventManager event
		 * @param {Number} quantity - Item quantity
		 * @returns {Object} GA4-formatted item
		 */
		transformEventItemToGA4Item(eventItem, quantity) {
			if (!eventItem || typeof eventItem !== 'object') {
				console.warn('[UFT GA4 Analytics] Invalid event data for transformation:', eventItem);
				return null;
			}

			// Use regular price (no finalPrice for product-manager-style structure)
			const priceInCents = eventItem.price || 0;

			const item = {
				item_id: String(eventItem.productId || ''),
				item_name: eventItem.productName || '',
				price: this.centsToPrice(priceInCents),
				quantity: quantity,
			};

			this.mapOptionalEventFieldsToGA4Item(eventItem, item);
			return item;
		}

		mapOptionalEventFieldsToGA4Item(eventItem, item) {
			// Optional fields
			const orgName = window.data?.organization_name;
			this.mapOptionalEventField(orgName, () => {
				item.affiliation = orgName;
				item.item_brand = orgName;
			});

			this.mapOptionalEventField(eventItem.variantTitle, () => (item.item_variant = eventItem.variantTitle));
			this.mapOptionalEventField(eventItem.discount, () => (item.discount = eventItem.discount));
			// prettier-ignore
			this.mapOptionalEventField(eventItem.available, () => (item.item_availability = eventItem.available ? 'in-stock' : 'oos'));
			this.mapOptionalEventField(eventItem.productType, () => (item.item_category = eventItem.productType));
			// prettier-ignore
			this.mapOptionalEventField(eventItem.itemSubscriptionFrequency, () => (item.item_subscription_frequency = eventItem.itemSubscriptionFrequency));
			this.mapOptionalEventField(eventItem.itemProductType, () => (item.item_product_type = eventItem.itemProductType));
		}

		mapOptionalEventField(field, setterFunc) {
			if (field != null) {
				setterFunc();
			}
		}

		pushToDataLayer(eventObject, isEcommerceEvent) {
			if (typeof window.dataLayer === 'undefined') {
				console.warn('[UFT GA4 Analytics] window.dataLayer is not available');
				return;
			}

			if (!eventObject || typeof eventObject !== 'object') {
				console.warn('[UFT GA4 Analytics] Invalid event object for dataLayer:', eventObject);
				return;
			}

			if (this.isGTMInitialized()) {
				this.executePush(eventObject, isEcommerceEvent);
				return;
			}

			this.waitForGtm(() => this.executePush(eventObject, isEcommerceEvent));
		}

		/**
		 * Wait for GTM to be available before executing callback
		 * Polls every 100ms, times out after 5 seconds
		 *
		 * @param {Function} callback - Function to execute when GTM is ready
		 */
		waitForGtm(callback) {
			const POLL_INTERVAL_MS = 100;
			const MAX_ATTEMPTS = 50;
			let attempts = 0;
			let intervalId = null;

			const checkGtm = () => {
				attempts++;

				if (this.isGTMInitialized()) {
					clearInterval(intervalId);
					this.log(`GTM initialized after ${attempts} attempt(s)`);
					callback();
					return;
				}

				if (attempts >= MAX_ATTEMPTS) {
					clearInterval(intervalId);
					console.warn('[UFT GA4 Analytics] GTM initialization timeout - event discarded');
				}
			};

			intervalId = setInterval(checkGtm, POLL_INTERVAL_MS);
		}

		isGTMInitialized() {
			return window.google_tag_manager != null;
		}

		/**
		 * Execute a datalayer push for an arbitrary event object
		 *
		 * @param {Object} eventObject - GA4 event object to push
		 * @param {Boolean} isEcommerceEvent - If this event is a GA4 Ecommerce Event
		 */
		executePush(eventObject, isEcommerceEvent) {
			try {
				if (isEcommerceEvent) {
					// Clear previous ecommerce data (GA4 best practice)
					window.dataLayer.push({ ecommerce: null });
				}
				window.dataLayer.push(eventObject);
				this.log('Pushed to dataLayer', eventObject);
			} catch (error) {
				console.error('[UFT GA4 Analytics] Error pushing to dataLayer:', error);
			}
		}

		validateEventData(data, requiredFields) {
			if (!data || typeof data !== 'object') {
				return false;
			}

			if (!Array.isArray(requiredFields)) {
				return false;
			}

			for (const field of requiredFields) {
				if (!data[field]) {
					return false;
				}
			}

			return true;
		}

		validateEventManager() {
			return window.UFT?.EventManager;
		}

		centsToPrice(cents) {
			if (typeof cents !== 'number' || isNaN(cents)) {
				return 0;
			}

			return parseFloat((cents / 100).toFixed(2));
		}

		getCurrency() {
			return window.data?.currency || 'USD';
		}

		log(message, data) {
			if (!this.debug) {
				return;
			}

			if (data) {
				console.log(`[UFT GA4 Analytics] ${message}`, data);
			} else {
				console.log(`[UFT GA4 Analytics] ${message}`);
			}
		}

		handleNavClickEvent(data) {
			const navEvent = this.createNavEvent(data);
			this.pushToDataLayer(navEvent, false);
		}

		createNavEvent(data) {
			const event = { event: 'navigation' };

			// Only include parameters with values (per requirements)
			if (data.clickText) {
				event.click_text = data.clickText;
			}
			if (data.elementLocation) {
				event.element_location = data.elementLocation;
			}
			if (data.cmsComponent) {
				event.cms_component = data.cmsComponent;
			}

			return event;
		}
	}

	return new GA4DataLayer();
})();

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => {
		window.UFT.Analytics.GA4DataLayer.init();
	});
} else {
	window.UFT.Analytics.GA4DataLayer.init();
}
